import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { FilterBar, PageHeader, SearchField, SelectField, plural } from "../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton } from "../components/States";
import { listCrew, listQuotes } from "../lib/data";
import { BRAND } from "../lib/config";
import { OverrideConfirm } from "../components/OverrideConfirm";
import { addDays, fmtDate, fmtDay, fmtTime, humanise, localHm, localYmd, weekday } from "../lib/format";
import {
  addShifts,
  allocate,
  allocateMany,
  createJob,
  getJob,
  jobDocumentBlob,
  listAcks,
  listJobs,
  openBlobInNewTab,
  setAllocationRole,
  setShiftStatus,
  unallocate,
  updateJob,
  updateShift,
  uploadJobDocument,
  type ShiftDraft,
} from "../lib/jobs-data";
import { hashParams, mapsUrl, telHref, useHash } from "../lib/route";
import { supabase, type CrewMember, type DocumentAck, type JobDetail, type JobDocument, type JobFields, type JobShift, type JobStatus } from "../lib/supabase";
import { useAsync } from "../lib/useAsync";
import { checklistsForShifts, isEndChecklistRequired } from "../modules/field/field-data";
import { cleanPermitHours, describePermitHours, PermitHoursEditor } from "../modules/field/PermitHours";
import { CompleteOverride, ShiftChecklistStatus } from "../modules/field/ShiftChecklistStatus";
import type { ShiftChecklistRow } from "../lib/supabase";
import { ClientPicker, JobConfirmation } from "../modules/clients/ClientParts";
import { CreateSwms, SwmsMissingWarning, SwmsSignoff, swmsTemplates } from "../modules/field/Swms";
import { docketsInUse, JobDockets } from "../modules/field/Docket";
import { JobBilling } from "../modules/billing/JobBilling";
import { executedSwmsPdf, signoffsForJob, type SwmsSignoffRow } from "../modules/field/swms-data";
import { InsuranceOverride, ShiftKitsCell } from "../modules/crew-records/Records";
import { ReplyMark, ShiftMessagesCell } from "../modules/crew-messages/Messages";
import { isInsuranceBlock } from "../modules/crew-records/records-data";

export const KIND_LABEL: Record<string, string> = {
  tmp: "Traffic management plan",
  tgs: "Traffic guidance scheme",
  permit: "Permit or ROL",
  swms: "SWMS",
  other: "Other",
};
export const ROLE_LABEL: Record<string, string> = { traffic_controller: "Traffic controller", leading_hand: "Leading hand" };
const STATUSES: JobStatus[] = ["planned", "active", "on_hold", "completed", "cancelled"];
const WORK_TYPES = ["Traffic and pedestrian management", "Traffic management", "Pedestrian management only", "Traffic control (stop/slow)"];

/** #/jobs, #/jobs/new and #/jobs/<id>. */
export function Jobs() {
  const [id] = hashParams(useHash());
  if (id === "new") return <NewJob />;
  if (id) return <JobView key={id} id={id} />;
  return <JobList />;
}

/* ------------------------------------------------------------------ list */

function JobList() {
  const { data, error, loading, reload } = useAsync(listJobs);
  const [q, setQ] = useState("");
  const [show, setShow] = useState("open");
  const now = new Date().toISOString();

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data ?? []).filter((j) => {
      if (show === "open" && (j.status === "completed" || j.status === "cancelled")) return false;
      if (show === "closed" && !(j.status === "completed" || j.status === "cancelled")) return false;
      return !needle || [j.reference, j.title, j.client_name, j.site_address].some((v) => (v ?? "").toLowerCase().includes(needle));
    });
  }, [data, q, show]);

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Operations"
        title="Jobs"
        meta="A job holds the shifts, crew, permit details and documents for one piece of work. Crew see a job pack for each shift they are on."
        actions={<a className="btn btn--primary" href="#/jobs/new"><Icon name="plus" size={18} /> New job</a>}
      />
      <FilterBar count={data ? `Showing ${plural(rows.length, "job")}` : undefined}>
        <SearchField label="Search jobs" placeholder="Search reference, job, client or address" value={q} onChange={setQ} />
        <SelectField label="Show" value={show} onChange={setShow} options={[{ value: "open", label: "Open jobs" }, { value: "closed", label: "Completed or cancelled" }, { value: "all", label: "All jobs" }]} />
      </FilterBar>
      {loading && !data && <Skeleton columns={6} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.length === 0 && <EmptyState help icon="document" title="No jobs yet">Create a job for each piece of work, then add its shifts, crew and documents.</EmptyState>}
      {data && data.length > 0 && rows.length === 0 && <EmptyState icon="search" title="No matching jobs">Try a different search or filter.</EmptyState>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--stack">
            <thead>
              <tr><th scope="col">Job</th><th scope="col">Client</th><th scope="col">Site</th><th scope="col">Next shift</th><th scope="col">Documents</th><th scope="col">Status</th></tr>
            </thead>
            <tbody>
              {rows.map((j) => {
                const live = j.bookings.filter((b) => b.status !== "cancelled");
                const next = live.filter((b) => b.ends_at >= now).sort((a, b) => a.starts_at.localeCompare(b.starts_at))[0];
                return (
                  <tr key={j.id}>
                    <td data-label="Job"><a className="strong" href={`#/jobs/${j.id}`}>{j.title}</a><span className="sub">{j.reference}</span></td>
                    <td data-label="Client">{j.client_name ?? <span className="muted">None</span>}</td>
                    <td data-label="Site">{j.site_address ?? <span className="muted">None</span>}</td>
                    <td data-label="Next shift">{next ? <span>{fmtDate(next.starts_at)}<span className="sub">{plural(live.length, "shift")} in total</span></span> : <span className="muted">{live.length ? "All done" : "No shifts"}</span>}</td>
                    <td data-label="Documents">{j.job_documents.filter((d) => !d.superseded_by).length}</td>
                    <td data-label="Status"><Chip value={j.status === "active" ? "active" : j.status === "planned" ? "draft" : "inactive"}>{humanise(j.status)}</Chip></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ create / edit */

type FormFields = Omit<JobFields, "status"> & { status?: JobStatus };
const EMPTY: FormFields = {
  title: "", site_address: "", client_name: "", site_contact_name: "", site_contact_phone: "", work_type: WORK_TYPES[0]!,
  permit_reference: "", permit_conditions: "", crew_notes: "", internal_notes: "", quote_id: null, approved_hours: "", permit_hours: null, customer_id: null, nearest_hospital: "", scope_of_work: "", site_contact_email: "",
};
const nullIfBlank = (f: FormFields): FormFields =>
  Object.fromEntries(Object.entries(f).map(([k, v]) => [k, typeof v === "string" ? (v.trim() || (k === "title" ? "" : null)) : v])) as FormFields;

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  return (
    <label className={`field${wide ? " field--wide" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

function JobForm({ initial, submitLabel, onSubmit, onCancel }: { initial: FormFields; submitLabel: string; onSubmit: (f: FormFields) => Promise<void>; onCancel?: () => void }) {
  const [f, setF] = useState<FormFields>(initial);
  const quotes = useAsync(BRAND.agreements ? listQuotes : async () => []);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  const set = (k: keyof FormFields) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!f.title.trim()) return setNotice({ tone: "error", text: "Give the job a name." });
    setBusy(true);
    try {
      await onSubmit({ ...nullIfBlank(f), permit_hours: cleanPermitHours(f.permit_hours) });
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="form-grid">
        <Field label="Job name" wide><input className="input" required value={f.title} onChange={set("title")} placeholder="e.g. Remedial works and crane lift" /></Field>
        <ClientPicker value={f.customer_id ?? null} onPick={(c) => setF((x) => ({ ...x, customer_id: c?.id ?? null, client_name: x.client_name || (c ? (c.trading_name ?? c.name) : x.client_name) }))} />
        <Field label="Client" hint="Who the work is for, as crew should know them"><input className="input" value={f.client_name ?? ""} onChange={set("client_name")} /></Field>
        <Field label="Site address"><input className="input" value={f.site_address ?? ""} onChange={set("site_address")} autoComplete="off" /></Field>
        <Field label="Site contact"><input className="input" value={f.site_contact_name ?? ""} onChange={set("site_contact_name")} /></Field>
        <Field label="Site contact phone"><input className="input" type="tel" value={f.site_contact_phone ?? ""} onChange={set("site_contact_phone")} /></Field>
        <Field label="Site contact email" hint="Where the SWMS goes for the client to sign"><input className="input" type="email" value={f.site_contact_email ?? ""} onChange={set("site_contact_email")} /></Field>
        <Field label="Type of work">
          <input className="input" list="work-types" value={f.work_type ?? ""} onChange={set("work_type")} />
          <datalist id="work-types">{WORK_TYPES.map((w) => <option key={w} value={w} />)}</datalist>
        </Field>
        <Field label="Permit or ROL number"><input className="input" value={f.permit_reference ?? ""} onChange={set("permit_reference")} /></Field>
        <Field label="Approved hours" hint="As crew should read them, e.g. Mon to Fri 8pm to 5am"><input className="input" value={f.approved_hours ?? ""} onChange={set("approved_hours")} /></Field>
        <Field label="Nearest hospital" hint="Emergency department and address. Printed on the SWMS."><input className="input" value={f.nearest_hospital ?? ""} onChange={set("nearest_hospital")} /></Field>
        <div className="field field--wide">
          <span className="field__label">Permit hours (for the start of shift check)</span>
          <PermitHoursEditor value={f.permit_hours} onChange={(v) => setF((x) => ({ ...x, permit_hours: v }))} />
          <span className="field__hint">Optional. When set, the leading hand sees whether the shift starts inside these hours.</span>
        </div>
        {BRAND.agreements && (
          <Field label="Quote the client signs" hint="Shifts stay unconfirmed until this quote is signed">
            <select className="input" value={f.quote_id ?? ""} onChange={(e) => setF((x) => ({ ...x, quote_id: e.target.value || null }))}>
              <option value="">No quote yet</option>
              {(quotes.data ?? []).map((q) => <option key={q.id} value={q.id}>{q.quote_number}{q.job_title ? `, ${q.job_title}` : ""}{q.client_name ? ` (${q.client_name})` : ""}{q.status === "accepted" ? ", signed" : ""}</option>)}
            </select>
          </Field>
        )}
        {f.status && (
          <Field label="Status">
            <select className="input" value={f.status} onChange={set("status")}>{STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}</select>
          </Field>
        )}
        <Field label="Scope of work" hint="Optional. Printed on the SWMS instead of the type of work, job and site." wide>
          <textarea className="input" rows={2} value={f.scope_of_work ?? ""} onChange={set("scope_of_work")} />
        </Field>
        <Field label="Permit conditions for crew" hint="One per line. Crew see these in the job pack." wide>
          <textarea className="input" rows={5} value={f.permit_conditions ?? ""} onChange={set("permit_conditions")} />
        </Field>
        <Field label="Notes for crew" hint="Parking, where to meet, anything they should know" wide>
          <textarea className="input" rows={3} value={f.crew_notes ?? ""} onChange={set("crew_notes")} />
        </Field>
        <Field label="Office notes" hint="Never shown to crew" wide>
          <textarea className="input" rows={3} value={f.internal_notes ?? ""} onChange={set("internal_notes")} />
        </Field>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>{busy ? "Saving" : submitLabel}</button>
        {onCancel && <button type="button" className="btn btn--outline" onClick={onCancel} disabled={busy}>Cancel</button>}
      </div>
    </form>
  );
}

function NewJob() {
  return (
    <section className="page" aria-labelledby="page-title">
      <a className="backlink" href="#/jobs"><Icon name="arrow" size={16} /> All jobs</a>
      <PageHeader eyebrow="Jobs" title="New job" meta="Start with the basics. You can add shifts, crew and documents on the next screen." />
      <div className="card"><div className="card__body">
        <JobForm initial={EMPTY} submitLabel="Create job" onSubmit={async (f) => { const id = await createJob(f); window.location.hash = `#/jobs/${id}`; }} />
      </div></div>
    </section>
  );
}

/* ------------------------------------------------------------------ detail */

function JobView({ id }: { id: string }) {
  const load = async () => {
    const [job, crew] = await Promise.all([getJob(id), listCrew()]);
    const current = job ? job.job_documents.filter((d) => !d.superseded_by) : [];
    const [acks, checklists, signoffs] = await Promise.all([
      listAcks(current.map((d) => d.id)),
      checklistsForShifts(job ? job.bookings.map((b) => b.id) : []),
      job && swmsTemplates().length ? signoffsForJob(job.id) : Promise.resolve([] as SwmsSignoffRow[]),
    ]);
    return { job, crew, acks, checklists, signoffs };
  };
  const { data, error, loading, reload } = useAsync(load, [id]);
  const [editing, setEditing] = useState(false);

  if (loading && !data) return <section className="page"><Skeleton rows={4} columns={3} /></section>;
  if (error) return <section className="page"><ErrorState message={error} onRetry={reload} /></section>;
  if (!data?.job) return <section className="page"><EmptyState icon="search" title="Job not found"><a href="#/jobs">Back to jobs</a></EmptyState></section>;
  const { job, crew, acks, checklists, signoffs } = data;

  return (
    <section className="page job-grid" aria-labelledby="page-title">
      <a className="backlink" href="#/jobs"><Icon name="arrow" size={16} /> All jobs</a>
      <PageHeader
        eyebrow={`${job.reference} · ${humanise(job.status)}`}
        title={job.title}
        meta={[job.client_name, job.site_address].filter(Boolean).join(" · ") || undefined}
        actions={!editing && <button type="button" className="btn btn--outline" onClick={() => setEditing(true)}>Edit details</button>}
      />

      <div className="card">
        <div className="card__head"><h2 className="card__title">Details</h2></div>
        <div className="card__body">
          {editing ? (
            <JobForm
              initial={{ ...EMPTY, ...stripJob(job) }}
              submitLabel="Save details"
              onCancel={() => setEditing(false)}
              onSubmit={async (f) => { await updateJob(job.id, f); setEditing(false); reload(); }}
            />
          ) : (
            <JobFacts job={job} />
          )}
        </div>
      </div>

      <JobConfirmation job={job} onDone={reload} />
      <Shifts job={job} crew={crew} checklists={checklists} onChange={reload} />
      <Documents job={job} crew={crew} acks={acks} signoffs={signoffs} onChange={reload} />
      {docketsInUse() && <JobDockets job={job} onChanged={reload} />}
      {docketsInUse() && <JobBilling job={job} />}
    </section>
  );
}

function stripJob(j: JobDetail): FormFields {
  return {
    title: j.title, status: j.status, site_address: j.site_address, client_name: j.client_name, site_contact_name: j.site_contact_name,
    site_contact_phone: j.site_contact_phone, work_type: j.work_type, permit_reference: j.permit_reference, permit_conditions: j.permit_conditions,
    crew_notes: j.crew_notes, internal_notes: j.internal_notes, quote_id: j.quote_id ?? null, approved_hours: j.approved_hours ?? null, permit_hours: j.permit_hours ?? null,
    customer_id: j.customer_id ?? null, nearest_hospital: j.nearest_hospital ?? null, scope_of_work: j.scope_of_work ?? null,
    site_contact_email: j.site_contact_email ?? null,
  };
}

function JobFacts({ job }: { job: JobDetail }) {
  const none = <span className="muted">Not set</span>;
  return (
    <dl className="facts">
      <div className="fact"><dt>Client</dt><dd>{job.client_name ?? none}</dd></div>
      <div className="fact"><dt>Site</dt><dd>{job.site_address ? <a href={mapsUrl(job.site_address)} target="_blank" rel="noreferrer">{job.site_address}</a> : none}</dd></div>
      <div className="fact"><dt>Site contact</dt><dd>{job.site_contact_name ?? none}{job.site_contact_phone && <> · <a className="tel" href={telHref(job.site_contact_phone)}>{job.site_contact_phone}</a></>}{job.site_contact_email && <> · {job.site_contact_email}</>}</dd></div>
      <div className="fact"><dt>Type of work</dt><dd>{job.work_type ?? none}</dd></div>
      <div className="fact"><dt>Permit or ROL</dt><dd>{job.permit_reference ?? none}</dd></div>
      <div className="fact"><dt>Approved hours</dt><dd>{job.approved_hours ?? none}{job.permit_hours?.length ? <span className="sub">Checked against: {describePermitHours(job.permit_hours)}</span> : null}</dd></div>
      <div className="fact"><dt>Nearest hospital</dt><dd>{job.nearest_hospital ?? none}</dd></div>
      {BRAND.agreements && <div className="fact"><dt>Signed quote</dt><dd>{job.quote_id ? <a href={`#/quotes/${job.quote_id}`}>Open the quote</a> : <span className="muted">No quote linked. Shifts stay unconfirmed until one is signed.</span>}</dd></div>}
      {job.scope_of_work && <div className="fact fact--wide"><dt>Scope of work</dt><dd>{job.scope_of_work}</dd></div>}
      <div className="fact fact--wide"><dt>Permit conditions for crew</dt><dd>{job.permit_conditions ?? none}</dd></div>
      <div className="fact fact--wide"><dt>Notes for crew</dt><dd>{job.crew_notes ?? none}</dd></div>
      <div className="fact fact--wide"><dt>Office notes</dt><dd>{job.internal_notes ?? none}</dd></div>
    </dl>
  );
}

/* ------------------------------------------------------------------ shifts */

const DAYS = [["1", "Mon"], ["2", "Tue"], ["3", "Wed"], ["4", "Thu"], ["5", "Fri"], ["6", "Sat"], ["0", "Sun"]] as const;

function datesBetween(from: string, to: string, days: Set<string>): string[] {
  const out: string[] = [];
  for (let d = from; d <= to && out.length < 120; d = addDays(d, 1)) if (days.has(String(weekday(d)))) out.push(d);
  return out;
}

function AddShifts({ job, onAdded }: { job: JobDetail; onAdded: () => void }) {
  const today = localYmd();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [days, setDays] = useState(new Set(["1", "2", "3", "4", "5"]));
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("16:00");
  const [crewSize, setCrewSize] = useState(2);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const dates = from && to && to >= from ? datesBetween(from, to, days) : [];

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!dates.length) return setNotice({ tone: "error", text: "Pick a date range and at least one day." });
    setBusy(true);
    try {
      const drafts: ShiftDraft[] = dates.map((date) => ({ date, start, end, crewSize }));
      await addShifts(job, drafts);
      ok(`Added ${plural(drafts.length, "shift")}.`);
      onAdded();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="form-grid">
        <Field label="From"><input className="input" type="date" required value={from} onChange={(e) => { setFrom(e.target.value); if (to < e.target.value) setTo(e.target.value); }} /></Field>
        <Field label="To"><input className="input" type="date" required value={to} min={from} onChange={(e) => setTo(e.target.value)} /></Field>
        <Field label="Start"><input className="input" type="time" required value={start} onChange={(e) => setStart(e.target.value)} /></Field>
        <Field label="Finish" hint="Earlier than the start means it finishes the next day"><input className="input" type="time" required value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
        <Field label="Crew needed"><input className="input" type="number" min={1} max={20} required value={crewSize} onChange={(e) => setCrewSize(Number(e.target.value))} /></Field>
      </div>
      <fieldset className="checks" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="field__label" style={{ marginBottom: 4 }}>Days</legend>
        {DAYS.map(([v, label]) => (
          <label key={v} className="check">
            <input type="checkbox" checked={days.has(v)} onChange={(e) => setDays((s) => { const n = new Set(s); if (e.target.checked) n.add(v); else n.delete(v); return n; })} /> {label}
          </label>
        ))}
      </fieldset>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={busy || !dates.length} aria-busy={busy}>
          <Icon name="plus" size={18} /> {busy ? "Adding" : dates.length ? `Add ${plural(dates.length, "shift")}` : "Add shifts"}
        </button>
        {dates.length > 0 && <span className="small muted">{dates.slice(0, 6).map((d) => fmtDay(`${d}T02:00:00Z`)).join(", ")}{dates.length > 6 ? " and more" : ""}</span>}
      </div>
    </form>
  );
}

function Shifts({ job, crew, checklists, onChange }: { job: JobDetail; crew: CrewMember[]; checklists: ShiftChecklistRow[]; onChange: () => void }) {
  const [adding, setAdding] = useState(job.bookings.length === 0);
  const [showPast, setShowPast] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const now = new Date().toISOString();
  const names = new Map(crew.map((m) => [m.id, m]));
  const active = crew.filter((m) => m.active);
  const upcoming = job.bookings.filter((b) => b.ends_at >= now && b.status !== "cancelled");
  const shown = job.bookings.filter((b) => showPast || b.ends_at >= now);

  async function run(fn: () => Promise<void>, done?: string) {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      if (done) ok(done);
      onChange();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  const [everyone, setEveryone] = useState("");
  const [everyoneRole, setEveryoneRole] = useState("traffic_controller");

  return (
    <div className="card">
      <div className="card__head">
        <h2 className="card__title">Shifts and crew</h2>
        <button type="button" className="btn btn--outline btn--sm" onClick={() => setAdding((a) => !a)}>{adding ? "Close" : <><Icon name="plus" size={16} /> Add shifts</>}</button>
      </div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        {adding && <AddShifts job={job} onAdded={() => { setAdding(false); onChange(); }} />}

        {upcoming.length > 1 && active.length > 0 && (
          <div className="addcrew">
            <span className="small strong">Put on every upcoming shift:</span>
            <select className="input" aria-label="Crew member for every upcoming shift" value={everyone} onChange={(e) => setEveryone(e.target.value)}>
              <option value="">Choose crew</option>
              {active.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
            </select>
            <select className="input" aria-label="Role" value={everyoneRole} onChange={(e) => setEveryoneRole(e.target.value)}>
              {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" className="btn btn--ink btn--sm" disabled={!everyone || busy} onClick={() => run(() => allocateMany(upcoming.map((b) => b.id), everyone, everyoneRole), `${names.get(everyone)?.full_name ?? "Crew member"} is on ${plural(upcoming.length, "shift")}.`)}>Add</button>
          </div>
        )}

        {job.bookings.length === 0 && !adding && <p className="muted">No shifts yet.</p>}
        {shown.length > 0 && (
          <div className="table-wrap table-wrap--flat">
            <table className="table table--stack">
              <thead><tr><th scope="col">Shift</th><th scope="col">Crew</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {shown.map((b) => <ShiftRow key={b.id} b={b} names={names} active={active} busy={busy} run={run} confirming={confirmRemove === b.id} setConfirming={setConfirmRemove} checklists={checklists} />)}
              </tbody>
            </table>
          </div>
        )}
        {job.bookings.some((b) => b.ends_at < now) && (
          <button type="button" className="btn btn--outline btn--sm" onClick={() => setShowPast((s) => !s)}>{showPast ? "Hide past shifts" : "Show past shifts"}</button>
        )}
      </div>
    </div>
  );
}

function ShiftRow({ b, names, active, busy, run, confirming, setConfirming, checklists }: {
  b: JobShift; names: Map<string, CrewMember>; active: CrewMember[]; busy: boolean;
  run: (fn: () => Promise<void>, done?: string) => Promise<void>; confirming: boolean; setConfirming: (id: string | null) => void;
  checklists: ShiftChecklistRow[];
}) {
  const [completing, setCompleting] = useState(false);
  const started = b.starts_at <= new Date().toISOString();
  const canComplete = started && (b.status === "confirmed" || b.status === "allocated");
  async function complete() {
    try {
      await setShiftStatus(b.id, "completed");
    } catch (e) {
      if (isEndChecklistRequired(e)) {
        setCompleting(true);
        return;
      }
      throw e;
    }
  }
  const [pick, setPick] = useState("");
  const [role, setRole] = useState("traffic_controller");
  const [blocked, setBlocked] = useState<string | null>(null);
  const on = new Set(b.allocations.map((a) => a.crew_member_id));
  const short = b.crew_size - b.allocations.length;
  const cancelled = b.status === "cancelled";
  const awaiting = b.status === "requested";
  const [overriding, setOverriding] = useState(false);
  // Change the shift's date and times (from-testing #44). The crew are told by the change notices.
  const [editing, setEditing] = useState<ShiftDraft | null>(null);
  const startEdit = () => setEditing({ date: localYmd(new Date(b.starts_at)), start: localHm(b.starts_at), end: localHm(b.ends_at), crewSize: b.crew_size });
  const ended = b.ends_at < new Date().toISOString();

  return (
    <tr className={cancelled ? "is-muted" : undefined}>
      <td data-label="Shift">
        <span className="strong">{fmtDay(b.starts_at)}</span>
        <span className="sub">{fmtTime(b.starts_at)} to {fmtTime(b.ends_at)} · {humanise(b.shift_type)}{cancelled ? " · Cancelled" : ""}</span>
        {awaiting && BRAND.agreements && (
          <span className="stack" style={{ gap: 6, marginTop: 6 }}>
            <Chip tone="accent">Awaiting signature</Chip>
            {!overriding && <button type="button" className="btn btn--outline btn--xs" onClick={() => setOverriding(true)}>Confirm anyway</button>}
          </span>
        )}
        {b.confirm_override_reason && <span className="sub sub--primary">Confirmed without signature: {b.confirm_override_reason}</span>}
        {editing && (
          <form className="stack" style={{ gap: 6, marginTop: 6 }} onSubmit={(e) => {
            e.preventDefault();
            const d = editing;
            void run(async () => { await updateShift(b.id, d); setEditing(null); }, BRAND.crewMessages ? "Shift changed. The crew are emailed and asked to confirm again." : "Shift changed.");
          }}>
            <label className="field"><span className="field__label">Date</span><input className="input" type="date" required value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></label>
            <div className="addcrew">
              <label className="field"><span className="field__label">Start</span><input className="input" type="time" required value={editing.start} onChange={(e) => setEditing({ ...editing, start: e.target.value })} /></label>
              <label className="field"><span className="field__label">Finish</span><input className="input" type="time" required value={editing.end} onChange={(e) => setEditing({ ...editing, end: e.target.value })} /></label>
              <label className="field"><span className="field__label">Crew</span><input className="input" type="number" min={1} max={50} required value={editing.crewSize} onChange={(e) => setEditing({ ...editing, crewSize: Number(e.target.value) })} /></label>
            </div>
            <span className="sub">A finish earlier than the start is the next morning.</span>
            <div className="addcrew">
              <button type="submit" className="btn btn--ink btn--xs" disabled={busy}>Save</button>
              <button type="button" className="btn btn--outline btn--xs" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </form>
        )}
        {overriding && (
          <OverrideConfirm bookingId={b.id} status="confirmed" label="This shift" onCancel={() => setOverriding(false)} onDone={() => { setOverriding(false); void run(async () => undefined, "Shift confirmed. The reason is recorded."); }} />
        )}
        {!cancelled && <ShiftChecklistStatus bookingId={b.id} rows={checklists} names={new Map([...names].map(([k, m]) => [k, m.full_name]))} />}
        {b.status === "completed" && <span className="sub"><Chip value="completed" /></span>}
        {b.complete_override_reason && <span className="sub sub--primary">Completed without the end of shift checklist: {b.complete_override_reason}</span>}
        {completing && (
          <CompleteOverride bookingId={b.id} onCancel={() => setCompleting(false)} onDone={() => { setCompleting(false); void run(async () => undefined, "Shift completed. The reason is recorded."); }} />
        )}
      </td>
      <td data-label="Crew">
        <div className="stack" style={{ gap: 8 }}>
          <div className="people">
            {b.allocations.map((a) => (
              <span key={a.id} className={`person${a.role === "leading_hand" ? " person--lead" : ""}`}>
                {names.get(a.crew_member_id)?.full_name ?? "Crew member"}
                {a.role === "leading_hand" && <Chip tone="ink">Lead</Chip>}
                {!cancelled && <ReplyMark response={a.response} reason={a.decline_reason} />}
                <button type="button" title={a.role === "leading_hand" ? "Make traffic controller" : "Make leading hand"} aria-label="Change role" disabled={busy}
                  onClick={() => run(() => setAllocationRole(a.id, a.role === "leading_hand" ? "traffic_controller" : "leading_hand"))}>
                  <Icon name="refresh" size={14} />
                </button>
                <button type="button" aria-label={`Take ${names.get(a.crew_member_id)?.full_name ?? "crew member"} off this shift`} disabled={busy} onClick={() => run(() => unallocate(a.id))}>
                  <Icon name="close" size={14} />
                </button>
              </span>
            ))}
            {short > 0 && !cancelled && <Chip tone="primary-outline">{short} more needed</Chip>}
          </div>
          {!cancelled && (
            <div className="addcrew">
              <select className="input" aria-label="Add crew to this shift" value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="">Add crew</option>
                {active.filter((m) => !on.has(m.id)).map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
              </select>
              <select className="input" aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
                {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button type="button" className="btn btn--ink btn--xs" disabled={!pick || busy} onClick={() => run(async () => {
                try {
                  await allocate(b.id, pick, role);
                  setPick("");
                } catch (e) {
                  if (!isInsuranceBlock(e)) throw e;
                  setBlocked(pick);
                }
              })}>Add</button>
            </div>
          )}
          {blocked && (
            <InsuranceOverride name={names.get(blocked)?.full_name ?? "This crew member"} busy={busy} onCancel={() => setBlocked(null)} onConfirm={(reason) => run(async () => { await allocate(b.id, blocked, role, reason); setBlocked(null); setPick(""); }, "Added. The reason is recorded.")} />
          )}
          <ShiftKitsCell bookingId={b.id} />
          <ShiftMessagesCell bookingId={b.id} />
        </div>
      </td>
      <td className="actions actions--stack">
        <a className="btn btn--outline btn--xs" href={`#/pack/${b.id}`}>Crew view</a>
        {!cancelled && !ended && !editing && <button type="button" className="btn btn--outline btn--xs" onClick={startEdit}>Change time</button>}
        {canComplete && <button type="button" className="btn btn--ink btn--xs" disabled={busy} onClick={() => void run(complete)}>Mark complete</button>}
        {confirming ? (
          <>
            <button type="button" className="btn btn--primary btn--xs" disabled={busy} onClick={() => run(() => removeShift(b.id), "Shift removed.").then(() => setConfirming(null))}>Yes, remove</button>
            <button type="button" className="btn btn--outline btn--xs" onClick={() => setConfirming(null)}>Keep</button>
          </>
        ) : (
          <button type="button" className="btn btn--outline btn--xs" onClick={() => setConfirming(b.id)}>Remove</button>
        )}
      </td>
    </tr>
  );
}

async function removeShift(id: string): Promise<void> {
  const { error } = await supabase.from("bookings").delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------------------------ documents */

const fmtSize = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function Upload({ job, replacing, onDone, onCancel }: { job: JobDetail; replacing: JobDocument | null; onDone: () => void; onCancel: () => void }) {
  const [kind, setKind] = useState<string>(replacing?.kind ?? "tmp");
  const [title, setTitle] = useState(replacing?.title ?? KIND_LABEL.tmp!);
  const [titleTouched, setTitleTouched] = useState(Boolean(replacing));
  const [version, setVersion] = useState("");
  const [requiresAck, setRequiresAck] = useState(replacing?.requires_ack ?? true);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!file) return setNotice({ tone: "error", text: "Choose a file." });
    setBusy(true);
    try {
      await uploadJobDocument(job.id, { file, kind, title, versionLabel: version, requiresAck: kind === "swms" ? false : requiresAck, replaces: replacing?.id });
      onDone();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {replacing && <p className="small">Replacing <strong>{replacing.title}</strong>{replacing.version_label ? ` (${replacing.version_label})` : ""}. Crew will be asked to sign the new version.</p>}
      <div className="form-grid">
        <Field label="Type">
          <select className="input" value={kind} disabled={Boolean(replacing)} onChange={(e) => { setKind(e.target.value); if (!titleTouched) setTitle(KIND_LABEL[e.target.value] ?? ""); }}>
            {Object.entries(KIND_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Title"><input className="input" required value={title} onChange={(e) => { setTitle(e.target.value); setTitleTouched(true); }} /></Field>
        <Field label="Version" hint="e.g. Version 3, Rev 0"><input className="input" value={version} onChange={(e) => setVersion(e.target.value)} /></Field>
        <Field label="File" hint="PDF, JPEG or PNG, up to 20 MB"><input className="input" type="file" accept="application/pdf,image/jpeg,image/png" required onChange={(e) => setFile(e.target.files?.[0] ?? null)} style={{ paddingTop: 9 }} /></Field>
      </div>
      {kind === "swms" ? (
        <p className="small muted">Crew sign on to a SWMS with their typed name, which replaces the paper sign-on sheet.</p>
      ) : (
        <label className="check"><input type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} /> Crew must confirm they have read it</label>
      )}
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>{busy ? "Uploading" : replacing ? "Upload new version" : "Upload"}</button>
        <button type="button" className="btn btn--outline" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function Documents({ job, crew, acks, signoffs, onChange }: { job: JobDetail; crew: CrewMember[]; acks: DocumentAck[]; signoffs: SwmsSignoffRow[]; onChange: () => void }) {
  const signoffOf = (docId: string) => signoffs.find((s) => s.document_id === docId);
  const [form, setForm] = useState<{ replacing: JobDocument | null } | null>(null);
  const [making, setMaking] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const canMake = swmsTemplates().length > 0;
  const current = job.job_documents.filter((d) => !d.superseded_by);
  const old = job.job_documents.filter((d) => d.superseded_by);
  const needing = current.filter((d) => d.kind === "swms" || d.requires_ack);
  const names = new Map(crew.map((m) => [m.id, m.full_name]));
  const people = [...new Set(job.bookings.filter((b) => b.status !== "cancelled").flatMap((b) => b.allocations.map((a) => a.crew_member_id)))];
  const ackOf = (doc: string, person: string) => acks.find((a) => a.document_id === doc && a.crew_member_id === person);

  const open = (d: JobDocument) => openBlobInNewTab(() => jobDocumentBlob(job.id, d.id)).catch(fail);

  return (
    <>
      <div className="card">
        <div className="card__head">
          <h2 className="card__title">Documents</h2>
          {!form && !making && (
            <div className="actions">
              {canMake && <button type="button" className="btn btn--outline btn--sm" onClick={() => { setNotice(null); setMaking(true); }}><Icon name="plus" size={16} /> Create SWMS</button>}
              <button type="button" className="btn btn--outline btn--sm" onClick={() => setForm({ replacing: null })}><Icon name="plus" size={16} /> Add document</button>
            </div>
          )}
        </div>
        <div className="card__body stack">
          <Notice notice={notice} onDismiss={() => setNotice(null)} />
          <SwmsMissingWarning documents={job.job_documents} />
          {making && <CreateSwms job={job} onCancel={() => setMaking(false)} onDone={(text) => { setMaking(false); ok(text); onChange(); }} />}
          {form && <Upload job={job} replacing={form.replacing} onCancel={() => setForm(null)} onDone={() => { setForm(null); ok("Document added. Crew see it in their job pack."); onChange(); }} />}
          {current.length === 0 && !form && !making && <p className="muted">No documents yet. Add the traffic management plan, traffic guidance schemes, permit and SWMS.</p>}
          {current.length > 0 && (
            <div className="table-wrap table-wrap--flat">
              <table className="table table--stack">
                <thead><tr><th scope="col">Document</th><th scope="col">Type</th><th scope="col">Crew signed</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
                <tbody>
                  {current.map((d) => {
                    const signs = d.kind === "swms" || d.requires_ack;
                    const signed = people.filter((p) => ackOf(d.id, p)).length;
                    return (
                      <tr key={d.id}>
                        <td data-label="Document"><span className="strong">{d.title}</span><span className="sub">{[d.version_label, fmtSize(d.size_bytes), `${d.template_key ? "made" : "added"} ${fmtDate(d.created_at)}${d.uploader?.full_name ? ` by ${d.uploader.full_name}` : ""}`].filter(Boolean).join(" · ")}</span></td>
                        <td data-label="Type">{KIND_LABEL[d.kind] ?? d.kind}{signoffOf(d.id) && <SwmsSignoff signoff={signoffOf(d.id)!} job={job} onChange={onChange} />}</td>
                        <td data-label="Crew signed">{signs ? (people.length ? `${signed} of ${people.length}` : <span className="muted">No crew yet</span>) : <span className="muted">Not needed</span>}</td>
                        <td className="actions actions--stack">
                          <button type="button" className="btn btn--outline btn--xs" onClick={() => void open(d)}>Open</button>
                          {d.template_key && canMake
                            ? <button type="button" className="btn btn--outline btn--xs" disabled={making} onClick={() => { setNotice(null); setMaking(true); }}>New version</button>
                            : <button type="button" className="btn btn--outline btn--xs" onClick={() => setForm({ replacing: d })}>New version</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {old.length > 0 && (
            <details>
              <summary className="small strong">Earlier versions ({old.length})</summary>
              <ul className="list">
                {old.map((d) => (
                  <li key={d.id} className="list__row">
                    <span>{d.title}{d.version_label ? ` (${d.version_label})` : ""} <span className="sub">added {fmtDate(d.created_at)}{signoffOf(d.id)?.client_signed_at ? ` · signed by client ${signoffOf(d.id)!.client_name}` : ""}</span></span>
                    <span className="actions">
                      <button type="button" className="btn btn--outline btn--xs" onClick={() => void open(d)}>Open</button>
                      {signoffOf(d.id)?.client_signed_at && <button type="button" className="btn btn--outline btn--xs" onClick={() => void openBlobInNewTab(() => executedSwmsPdf(signoffOf(d.id)!.id)).catch(fail)}>Executed copy</button>}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>

      {needing.length > 0 && people.length > 0 && (
        <div className="card">
          <div className="card__head"><h2 className="card__title">Crew sign-off</h2></div>
          <div className="card__body">
            <p className="small muted" style={{ marginBottom: 12 }}>Who has read each document and signed on to the SWMS, from their phones. Times are when they signed.</p>
            <div className="table-wrap table-wrap--flat">
              <table className="table signoff">
                <thead><tr><th scope="col">Crew</th>{needing.map((d) => <th key={d.id} scope="col">{d.title}{d.version_label ? ` (${d.version_label})` : ""}</th>)}</tr></thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p}>
                      <th scope="row">{names.get(p) ?? "Crew member"}</th>
                      {needing.map((d) => {
                        const a = ackOf(d.id, p);
                        return <td key={d.id}>{a ? <span className="tick" title={a.signed_name ? `Signed as ${a.signed_name}` : undefined}>{fmtDate(a.acknowledged_at)}</span> : <Chip tone="primary-outline">Not yet</Chip>}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Used by AddShifts' preview text and tests of local times. */
export const shiftLabel = (s: { starts_at: string; ends_at: string }) => `${fmtDay(s.starts_at)} ${localHm(s.starts_at)} to ${localHm(s.ends_at)}`;
